import { ReactElement, useEffect, useId, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { IPrefilter, describeAge, describeGender } from "./prefiltradoTypes";

// Tarjeta flotante con las condiciones de un prefiltro (comunas, edad, cargos,
// género y turno). Aparece al pasar el mouse por el botón del cliente o por
// cada sede del menú.

const FONT = "Roboto, sans-serif";
const OPEN_EVENT = "detalle-prefiltro:open";
const C = {
  slate: "#64748b",
  ink: "#1e293b",
  line: "#e2e8f0",
  chipBg: "#f1f5f9",
};

const describeShifts = (prefilter: IPrefilter): string => {
  const list = prefilter.shifts.length > 0 ? prefilter.shifts.join(", ") : "Todos";
  return prefilter.shiftExclusive ? list : `${list} (informativo)`;
};

const Fila = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 8, padding: "6px 0", borderTop: `1px solid ${C.line}` }}>
    <span style={{ color: C.slate, fontSize: 11.5, fontWeight: 500, paddingTop: 1 }}>{label}</span>
    <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.45 }}>{children}</div>
  </div>
);

const Chips = ({ items, empty }: { items: string[]; empty: string }) =>
  items.length === 0 ? (
    <span>{empty}</span>
  ) : (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {items.map((item) => (
        <span key={item} style={{ padding: "1px 7px", borderRadius: 999, background: C.chipBg, fontSize: 11.5 }}>
          {item}
        </span>
      ))}
    </div>
  );

export const TarjetaPrefiltro = ({ prefilter }: { prefilter: IPrefilter }) => {
  const localities = prefilter.localities.map((locality) => locality.name).sort((a, b) => a.localeCompare(b, "es"));
  return (
    <div style={{ fontFamily: FONT, color: C.ink, width: 360 }}>
      <div style={{ fontSize: 13, fontWeight: 600, paddingBottom: 6 }}>
        {prefilter.client ?? prefilter.clientId} · {prefilter.name}
      </div>
      <Fila label={`Comunas${localities.length ? ` (${localities.length})` : ""}`}>
        <Chips items={localities} empty="Todas" />
      </Fila>
      <Fila label="Edad">{describeAge(prefilter)}</Fila>
      <Fila label="Cargos">
        <Chips items={prefilter.positions} empty="Todos" />
      </Fila>
      <Fila label="Género">{describeGender(prefilter)}</Fila>
      <Fila label="Turno">
        {describeShifts(prefilter)}
        {/* Jornada y horarios del Excel del cliente: solo informativos, no filtran. */}
        {(prefilter.workday || prefilter.schedule) && (
          <div style={{ marginTop: 4, fontSize: 11.5, color: C.slate, lineHeight: 1.5 }}>
            {prefilter.workday && <div style={{ fontWeight: 500 }}>Jornada: {prefilter.workday}</div>}
            {/* Con muchos horarios (Blue Express tiene 31) se reparten en dos columnas. */}
            {prefilter.schedule && (
              <div style={{ whiteSpace: "pre-line", columns: prefilter.schedule.split("\n").length > 8 ? 2 : 1, columnGap: 12 }}>
                {prefilter.schedule}
              </div>
            )}
          </div>
        )}
      </Fila>
    </div>
  );
};

interface DetallePrefiltroProps {
  content: React.ReactNode | null;
  placement?: "bottom-start" | "right-start";
  /** Mientras es true la tarjeta se cierra y no se abre (p. ej. con el menú de sedes abierto). */
  disabled?: boolean;
  children: ReactElement;
}

/**
 * Envuelve un botón para mostrar `content` al pasar el mouse. Sin contenido, no
 * muestra nada. La tarjeta no recibe el mouse (disableInteractive): así nunca
 * tapa ni bloquea los botones o sedes que quedan debajo.
 *
 * Solo hay una tarjeta abierta a la vez: al abrirse una, las demás se cierran.
 * También se cierran con cualquier clic (p. ej. al abrir el menú o el editor),
 * con Escape, al hacer scroll o al salir de la ventana.
 */
const DetallePrefiltro = ({ content, placement = "bottom-start", disabled = false, children }: DetallePrefiltroProps) => {
  const id = useId();
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!hover) return undefined;
    const close = () => setHover(false);
    const onOtherOpen = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener(OPEN_EVENT, onOtherOpen);
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOtherOpen);
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("blur", close);
    };
  }, [hover, id]);

  const open = () => {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: id }));
    setHover(true);
  };

  return (
    <Tooltip
      title={content ?? ""}
      placement={placement}
      open={hover && !disabled && Boolean(content)}
      onOpen={open}
      onClose={() => setHover(false)}
      enterDelay={250}
      enterNextDelay={150}
      disableInteractive
      disableTouchListener
      PopperProps={{ modifiers: [{ name: "offset", options: { offset: [0, 10] } }] }}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            background: "#fff",
            color: C.ink,
            border: `1px solid ${C.line}`,
            borderRadius: "10px",
            boxShadow: "0 10px 25px rgba(15,23,42,0.14)",
            padding: "10px 12px",
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default DetallePrefiltro;
